// Frontend image compression for ID-capture uploads (fix for HTTP 413
// "payload too large" on phone photos, 2026-09-09).
//
// Downscales via canvas to a max dimension (default 1600px), re-encodes as
// JPEG at ~0.8 quality. Canvas re-encoding also strips EXIF (including GPS)
// implicitly — only raw pixels survive.
//
// Returns a JPEG data URL ready to be POSTed as `image` (data URL) to
// /access/id-capture.

export interface CompressOptions {
  maxDimension?: number;
  quality?: number;
}

export async function compressImageToDataUrl(
  file: File | Blob,
  opts: CompressOptions = {},
): Promise<string> {
  const maxDimension = opts.maxDimension ?? 1600;
  const quality = opts.quality ?? 0.8;

  const bitmapUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('No se pudo leer la imagen.'));
      el.src = bitmapUrl;
    });

    const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas no disponible en este navegador.');
    ctx.drawImage(img, 0, 0, w, h);

    // GIF/WEBP/PNG with transparency would lose it as JPEG, but for ID
    // photos JPEG is the right tradeoff for size.
    return canvas.toDataURL('image/jpeg', quality);
  } finally {
    URL.revokeObjectURL(bitmapUrl);
  }
}

// ---------------------------------------------------------------------------
// UX fix 2026-09-22 (Jaime): chat photo send (Wall/Mensajes) failed with
// "supera el límite de 1.5 MB" on typical 2-6 MB phone photos. This wrapper
// downscales to JPEG and retries with progressively lower quality/dimension
// until the base64 payload fits under the server cap (1.5 MB of base64 chars,
// which is ~1.1 MB of raw bytes). Marker: UXFIX-IMGCOMPRESS-20260922
// ---------------------------------------------------------------------------
export const MAX_ATTACHMENT_BASE64 = 1.5 * 1024 * 1024;

export async function compressImageUnderCap(
  file: File | Blob,
  opts: CompressOptions = {},
): Promise<string> {
  const attempts = [
    { maxDimension: opts.maxDimension ?? 1600, quality: opts.quality ?? 0.8 },
    { maxDimension: 1280, quality: 0.7 },
    { maxDimension: 1024, quality: 0.6 },
    { maxDimension: 800, quality: 0.5 },
  ];
  let lastErr: unknown = null;
  for (const attempt of attempts) {
    try {
      const dataUrl = await compressImageToDataUrl(file, attempt);
      if (dataUrl.length <= MAX_ATTACHMENT_BASE64) return dataUrl;
      lastErr = new Error('compressed image still over cap');
    } catch (err) {
      lastErr = err;
      break; // decode/canvas errors won't improve on retry
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('No se pudo comprimir la imagen.');
}
